import type { CSSProperties } from "@builder.io/qwik";
import {
  component$,
  Slot,
  useComputed$,
  useOnWindow,
  useSignal,
} from "@builder.io/qwik";
import type { UiContainer } from "~/models/ui";
import { SidebarTVGuide } from "./components/TvGuide";
import { useRafDebouncer$ } from "~/components/utils/rafDebouncer";
import { SidebarSocial } from "./components/Social";
import { SidebarMovies } from "./components/Movies";
import { SidebarSocialRows } from "./components/SocialRows";
import { SidebarRelatedPrograms } from "./components/RelatedPrograms";
import { SidebarBreakingNews } from "./components/BreakingNews";
import { SidebarStoreBadges } from "./components/StoreBadges";
import { useWindowScroll } from "~/hooks/ui/useWindowScroll";
import { OFFSET_BOTTOM, OFFSET_TOP } from "./constants";
type SidebarComponent = typeof SidebarRoot & {
  TvGuide: typeof SidebarTVGuide;
  Social: typeof SidebarSocial;
  Movies: typeof SidebarMovies;
  SocialRows: typeof SidebarSocialRows;
  RelatedPrograms: typeof SidebarRelatedPrograms;
  BreakingNews: typeof SidebarBreakingNews;
  StoreBadges: typeof SidebarStoreBadges;
};

const SidebarRoot = component$<UiContainer>(({ class: className }) => {
  const contentRef = useSignal<HTMLDivElement>();

  const { directions } = useWindowScroll();

  const stickyDirection = useSignal<"top" | "bottom">("bottom");

  // We use margins to limit the space available for the position: sticky
  // to make the sidebar "walk". It's like saving the current progress
  const topMargin = useSignal(0); // used when sticking to bottom
  const bottomMargin = useSignal(0); // used when sticking to top

  const inlineStyles = useComputed$<CSSProperties>(() => {
    switch (stickyDirection.value) {
      case "top":
        return {
          top: OFFSET_TOP,
          marginBottom: `${bottomMargin.value}px`,
          bottom: "auto",
        };
      case "bottom":
        return {
          top: "auto",
          bottom: `${OFFSET_BOTTOM}px`,
          marginTop: `${topMargin.value}px`,
        };
    }
  });

  const handleScroll = useRafDebouncer$(() => {
    if (!contentRef.value || !contentRef.value.offsetParent) return;
    const exceedingHeight = window.innerHeight - contentRef.value.clientHeight;

    if (exceedingHeight < 0) {
      // Only sidebars that exceed the viewport in height
      const spaceAvailable =
        contentRef.value.offsetParent.clientHeight -
        contentRef.value.clientHeight;

      topMargin.value = contentRef.value.offsetTop;
      bottomMargin.value = spaceAvailable - contentRef.value.offsetTop;

      if (directions.top) {
        stickyDirection.value = "top";
      } else if (directions.bottom) {
        stickyDirection.value = "bottom";
      }
    } else {
      // Otherwise, stick always to top
      stickyDirection.value = "top";
      bottomMargin.value = 0;
    }
  });
  useOnWindow("DOMContentLoaded", handleScroll);
  useOnWindow("scroll", handleScroll);
  useOnWindow("resize", handleScroll);
  return (
    <aside class={[className, "print:sgtv-hidden"]}>
      <div
        class={[
          "sgtv-relative sgtv-flex sgtv-h-full sgtv-flex-col",
          { "sgtv-justify-end": stickyDirection.value === "bottom" },
        ]}
      >
        <div
          ref={contentRef}
          class={["sgtv-inset-0 md:sgtv-sticky"]}
          style={inlineStyles.value}
        >
          <Slot />
        </div>
      </div>
    </aside>
  );
});
export const Sidebar = SidebarRoot as SidebarComponent;
Sidebar.TvGuide = SidebarTVGuide;
Sidebar.Social = SidebarSocial;
Sidebar.Movies = SidebarMovies;
Sidebar.SocialRows = SidebarSocialRows;
Sidebar.RelatedPrograms = SidebarRelatedPrograms;
Sidebar.BreakingNews = SidebarBreakingNews;
Sidebar.StoreBadges = SidebarStoreBadges;
